import { create } from 'zustand';
import { filePan } from '@strategy4kids/audio';
import {
  createGameAdapter,
  IllegalMoveError,
  pickStyledMove,
  styleMovetimeMs,
  STYLE_MULTI_PV,
  type GameAdapter,
  type MoveEngine,
} from '@strategy4kids/game-engine';
import type { PlayStyle } from '@strategy4kids/persona';
import { getCoachAdvice, type CoachAdvice } from '@strategy4kids/ai-coach';
import { HEATMAP_MODES, type Fen, type GameId, type HeatmapMode, type PositionSnapshot, type RobotLevel, type Turn } from '@strategy4kids/protocol';
import { useThemeStore } from './theme-store.js';
import { useToastStore } from './toast-store.js';
import { playSfx, setAmbientMood } from '../services/audio.js';

export type Phase = 'ready' | 'playing' | 'gameover';

export interface SquareTarget {
  readonly square: string;
  readonly capture: boolean;
}

interface GameStore {
  adapter: GameAdapter;
  gameId: GameId;
  engine: MoveEngine | null;
  level: RobotLevel | null;
  pos: PositionSnapshot;
  history: PositionSnapshot[];
  selected: string | null;
  targets: SquareTarget[];
  heatmap: HeatmapMode;
  /**
   * Which colour the human is playing, captured once at `startGame` from
   * whoever is to move.
   *
   * This exists because `pos.turn` is NOT the answer. The shield overlays
   * answer "how safe are *my* things", and `pos.turn` alternates every ply —
   * keying off it makes the badges jump to the robot's pieces on the robot's
   * turn, which reads as a glitch rather than as information. (Today the flip
   * is mostly hidden behind the robot-thinking banner, which is exactly the
   * kind of accidental safety net that stops being one.)
   *
   * It is also what makes "what is protecting *that* enemy piece?" answerable:
   * revealing an enemy square needs the *opponent's* shield map, since the
   * viewer's map never records enemy pieces as subjects.
   */
  playerColor: Turn;
  /**
   * The robot opponent's numeric playing style (P5), snapshotted at
   * `startGame` from the chosen persona — exactly like `level`, so changing
   * the persona mid-game affects the NEXT game and `restart()` keeps it.
   * `null` = unstyled: `robotMove` then requests and plays precisely what it
   * did before P5 existed (single candidate, raw level movetime).
   */
  robotStyle: PlayStyle | null;
  phase: Phase;
  robotThinking: boolean;
  coach: CoachAdvice[] | null;
  coachLoading: boolean;
  error: string | null;
  lastMove: { from: string; to: string } | null;
  checkSquare: string | null;
  /** Square whose shield / threat detail is expanded, set by `tap()` and
   *  cleared on move commit, deselect, undo and restart.
   *
   *  Purely additive to `selected`: revealing what threatens an ENEMY piece
   *  deliberately leaves `selected` alone, so "what is protecting that man?"
   *  can be asked without disturbing a half-planned move. The two fields
   *  answer different questions — `selected` is "what am I about to move",
   *  `reveal` is "what am I looking at" — and a tap can set either, both, or
   *  neither.
   *
   *  There is no "inspect mode" and deliberately so: tapping a piece is the
   *  only verb in the game, so making the reveal the same gesture keeps it
   *  undiscoverable-wrong. A mode toggle would be one more thing to notice and
   *  remember per question. */
  reveal: string | null;
  /** Look-ahead: the engine's principal variation after the player's
   *  intended move (set on `tap()`, cleared on commit / undo / restart /
   *  deselect). The chain alternates opponent / player / opponent ... and
   *  drives the cyan opponent + mint player highlight rings. Capped by
   *  `theme-store.lookaheadDepth`. */
  predictedReply: ReadonlyArray<{ from: string; to: string }> | null;

  startGame(
    gameId: GameId,
    engine: MoveEngine,
    level: RobotLevel,
    initialFen?: Fen,
    robotStyle?: PlayStyle | null,
  ): void;
  tap(square: string): void;
  move(from: string, to: string): void;
  robotMove(): void;
  undo(): void;
  restart(): void;
  cycleHeatmap(): void;
  askCoach(): Promise<void>;
  predictOpponentReply(): Promise<void>;
}

type GameStoreState = Omit<
  GameStore,
  | 'startGame'
  | 'tap'
  | 'move'
  | 'robotMove'
  | 'undo'
  | 'restart'
  | 'cycleHeatmap'
  | 'askCoach'
  | 'predictOpponentReply'
>;

const initialState: GameStoreState = {
  adapter: createGameAdapter('chess'),
  gameId: 'chess',
  engine: null,
  level: null,
  pos: createGameAdapter('chess').newGame(),
  history: [],
  selected: null,
  targets: [],
  heatmap: 'off',
  playerColor: 'w',
  robotStyle: null,
  phase: 'ready',
  robotThinking: false,
  coach: null,
  coachLoading: false,
  error: null,
  lastMove: null,
  checkSquare: null,
  reveal: null,
  predictedReply: null,
};

// Monotonic generation counter. Bumped on startGame/undo/restart so an in-flight
// robot or coach response from a superseded position is discarded instead of
// corrupting state.
let epoch = 0;

function pushState(state: GameStore, next: PositionSnapshot): Partial<GameStore> {
  const last = state.adapter.history(next).at(-1);
  const lastMove = last ? { from: last.from, to: last.to } : null;
  return {
    pos: next,
    history: [...state.history, next],
    selected: null,
    targets: [],
    lastMove,
    reveal: null,
    checkSquare:
      next.result == null && state.adapter.isCheck(next)
        ? state.adapter.kingSquare(next, next.turn)
        : null,
    // Committing a move (or robot's reply) invalidates the look-ahead.
    predictedReply: null,
  };
}

export const useGameStore = create<GameStore>()((set, get) => ({
  ...initialState,

  startGame(gameId, engine, level, initialFen, robotStyle) {
    setAmbientMood(gameId === 'checkers' ? 'checkers' : 'chess');
    const adapter = createGameAdapter(gameId);
    const pos = initialFen ? adapter.fromFen(initialFen) : adapter.newGame();
    epoch += 1;
    set({
      adapter,
      gameId,
      engine,
      level,
      robotStyle: robotStyle ?? null,
      pos,
      history: [pos],
      selected: null,
      targets: [],
      reveal: null,
      heatmap: 'off',
      playerColor: pos.turn,
      phase: 'playing',
      robotThinking: false,
      coach: null,
      coachLoading: false,
      error: null,
      lastMove: null,
      predictedReply: null,
      checkSquare:
        pos.result == null && adapter.isCheck(pos) ? adapter.kingSquare(pos, pos.turn) : null,
    });
    void engine.setPosition(pos.fen);
    void engine.setStrength(level.elo, { skill: level.skill });
    if (pos.turn === 'b') void get().robotMove();
  },

  tap(square) {
    const { pos, selected, targets, phase, robotThinking, adapter } = get();
    if (phase === 'gameover' || robotThinking) return;
    const piece = adapter.pieceAt(pos, square);
    const isOwn = piece !== null && piece.color === pos.turn;

    if (selected === null) {
      if (isOwn) {
        // Selecting a piece also expands its shields. Same gesture, both
        // answers — there is deliberately no separate "inspect" action.
        set({
          selected: square,
          reveal: square,
          targets: adapter
            .legalMovesForSquare(pos, square)
            .map((t) => ({ square: t.to, capture: t.capture })),
          predictedReply: null,
        });
        // Fire look-ahead async — see predictOpponentReply for the algorithm.
        void get().predictOpponentReply();
        return;
      }
      // Enemy piece → reveal what threatens it. Empty square → dismiss.
      set({ reveal: piece ? square : null });
      return;
    }

    if (square === selected) {
      set({ selected: null, targets: [], reveal: null, predictedReply: null });
      return;
    }

    const target = targets.find((t) => t.square === square);
    if (target) {
      // `move` → `pushState` clears `reveal` alongside `selected`.
      void get().move(selected, square);
      return;
    }

    if (isOwn) {
      set({
        selected: square,
        reveal: square,
        targets: adapter
          .legalMovesForSquare(pos, square)
          .map((t) => ({ square: t.to, capture: t.capture })),
        predictedReply: null,
      });
      // Re-selecting a different own piece re-runs the look-ahead against
      // the new selection's legal targets.
      void get().predictOpponentReply();
      return;
    }

    // Tapping an enemy piece or an empty non-destination used to be a silent
    // no-op. Both are now meaningful: the first is the only way to ask "what
    // can take this?" and the second dismisses the current reveal. Either is
    // an improvement for a kid who never uses shields, which is the point —
    // the board should have no dead taps.
    if (piece) set({ reveal: square });
    else set({ reveal: null });
  },

  async move(from, to) {
    const { adapter, engine, pos } = get();
    let next: PositionSnapshot;
    try {
      next = adapter.makeMove(pos, from, to);
    } catch (err) {
      set({ error: err instanceof IllegalMoveError ? 'That move is not allowed.' : String(err) });
      return;
    }
    const patch = pushState(get(), next);
    set({ ...patch, error: null });
    // Surface the player's move as a brief toast. The robot's reply gets its
    // own toast below; game-over transitions get a success/warning toast.
    const last = adapter.history(next).at(-1);
    if (last) {
      useToastStore.getState().push({
        title: 'Move played',
        body: last.san,
        kind: 'info',
        durationMs: 2400,
      });
      // The piece is heard where it lands, on its file (spec P2: board-file panning).
      playMoveSfx(last.san, get().gameId, filePan(to[0]));
    }
    if (engine) void engine.setPosition(next.fen);
    if (next.result) {
      set({ phase: 'gameover' });
      playGameoverSfx(resultToastTitle(next, pos.turn));
      useToastStore.getState().push({
        title: resultToastTitle(next, pos.turn),
        kind: 'success',
        durationMs: 4000,
      });
      return;
    }
    if (next.turn === 'b') void get().robotMove();
  },

  async robotMove() {
    const { adapter, engine, level, pos, robotStyle } = get();
    if (!engine || !level) return;
    const e = epoch;
    set({ robotThinking: true, error: null });
    try {
      // P5 playing styles: with a style, ask for MultiPV candidates and
      // re-rank inside the centipawn window (the strength bound). Without one
      // this is byte-identical to the pre-P5 call — same n, same movetime.
      const best = await engine.getBestMoves(robotStyle ? STYLE_MULTI_PV : 1, {
        movetimeMs: robotStyle ? styleMovetimeMs(level, robotStyle) : level.movetimeMs,
      });
      if (e !== epoch) return;
      const mv = robotStyle ? pickStyledMove(adapter, pos, best, robotStyle) : best[0];
      if (!mv) throw new Error('no move');
      const from = mv.uci.slice(0, 2);
      const to = mv.uci.slice(2, 4);
      const next = adapter.makeMove(pos, from, to);
      const patch = pushState(get(), next);
      set({ ...patch });
      // Robot reply toast — short so it doesn't compete with the move ticker.
      const last = adapter.history(next).at(-1);
      if (last) {
        useToastStore.getState().push({
          title: 'Robot replied',
          body: last.san,
          kind: 'info',
          durationMs: 2400,
        });
        // The robot's piece moves on the board too, so its sound is heard from the file it lands on.
        playMoveSfx(last.san, get().gameId, filePan(to[0]));
      }
      if (next.result) {
        set({ phase: 'gameover' });
        playGameoverSfx(resultToastTitle(next, pos.turn));
        useToastStore.getState().push({
          title: resultToastTitle(next, pos.turn),
          kind: pos.turn === 'w' ? 'warning' : 'success',
          durationMs: 4000,
        });
      }
    } catch {
      if (e === epoch)
        set({ error: 'Robot is having trouble. You can keep playing with a friend!' });
    } finally {
      if (e === epoch) set({ robotThinking: false });
    }
  },

  undo() {
    const { engine, history } = get();
    if (history.length <= 1) return;
    // Always land back on the player's turn: if the top snapshot is black-to-move,
    // the last move was the player's (white) → undo 1; if white-to-move, the last
    // move was the robot's (black) → undo 2 to return to the player.
    const steps = history[history.length - 1]?.turn === 'w' ? 2 : 1;
    // We only enter this branch when `history.length > 1`, so `history.length - 1 - steps`
    // is at most `history.length - 2`, and `Math.max(0, …)` yields a valid in-range index.
    const next = history[Math.max(0, history.length - 1 - steps)];
    if (!next) return;
    epoch += 1;
    set({
      pos: next,
      history: history.slice(0, Math.max(1, history.length - steps)),
      selected: null,
      targets: [],
      phase: 'playing',
      coach: null,
      error: null,
      robotThinking: false,
      lastMove: null,
      checkSquare: null,
      reveal: null,
      predictedReply: null,
    });
    if (engine) void engine.setPosition(next.fen);
  },

  restart() {
    const { adapter, engine, level, playerColor, robotStyle } = get();
    const pos = adapter.newGame();
    epoch += 1;
    // `playerColor` is carried over explicitly: `...initialState` would reset
    // it to 'w', which is right by accident today and wrong the moment the
    // picker offers "play as black". `robotStyle` joins it for the same
    // reason: a restart must not silently un-style the opponent.
    set({
      ...initialState,
      adapter,
      engine,
      level,
      robotStyle,
      pos,
      history: [pos],
      phase: 'playing',
      playerColor,
    });
    if (engine) void engine.setPosition(pos.fen);
  },

  cycleHeatmap() {
    // `HEATMAP_MODES` is simultaneously the cycle and the type of `heatmap`, so
    // a mode cannot reach the UI without being orderable here, and there is no
    // second list to forget to update. A stored value outside the cycle (a
    // persisted older build, say) lands on index -1, which advances to 0 = 'off'.
    const i = HEATMAP_MODES.indexOf(get().heatmap);
    const next: HeatmapMode = HEATMAP_MODES[(i + 1) % HEATMAP_MODES.length] ?? 'off';
    set({ heatmap: next });
  },

  async askCoach() {
    const { adapter, engine, pos, level } = get();
    if (!engine || !level) return;
    const e = epoch;
    set({ coachLoading: true, coach: null, error: null });
    try {
      await engine.setStrength(level.elo, { skill: level.skill });
      const advice = await getCoachAdvice({
        engine,
        adapter,
        fen: pos.fen,
        turn: pos.turn,
        count: 3,
      });
      if (e !== epoch) return;
      set({ coach: advice });
    } catch {
      if (e === epoch) set({ error: 'The coach is thinking too hard. Try again in a moment.' });
    } finally {
      if (e === epoch) set({ coachLoading: false });
    }
  },

  /**
   * Predict the opponent's most likely reply to the player's intended move.
   *
   * Algorithm:
   *  1. Rank the player's legal targets by how good they are FOR the player
   *     (`adapter.evaluate` from the player's perspective: ascending when the
   *     player is black, descending when white — the engine's eval is from
   *     white's POV). The top-ranked target is what the player would
   *     "intend" to play.
   *  2. Make the player's move, then ask the engine for black's best reply.
   *     Walk `bm.pv` (the engine's principal variation after the player's
   *     intended move) and push each UCI token as `{from, to}` onto the
   *     chain. The chain is capped by `theme-store.lookaheadDepth` — the
   *     engine self-imposes a movetime budget, so `bm.pv.length` may be
   *     smaller than the configured depth, never larger.
   *
   * 200ms movetime keeps the prediction fast — it feels instantaneous. A
   * stale epoch (a tap() that fired just before undo() / restart()) bails
   * out without writing state.
   */
  async predictOpponentReply() {
    const { lookaheadEnabled, lookaheadDepth } = useThemeStore.getState();
    if (!lookaheadEnabled) return;
    const { pos, selected, targets, engine, adapter, robotThinking } = get();
    if (!selected || !engine || pos.result || robotThinking) return;
    if (targets.length === 0) {
      set({ predictedReply: null });
      return;
    }
    // Rank targets by evaluation from the player's POV.
    const playerIsWhite = pos.turn === 'w';
    const scored = targets.map((t) => {
      const after = adapter.makeMove(pos, selected, t.square);
      // Eval is from white's POV; invert when player is black so the highest
      // score is always "best for the player about to move".
      const score = playerIsWhite ? adapter.evaluate(after) : -adapter.evaluate(after);
      return { t, after, score };
    });
    scored.sort((a, b) => b.score - a.score);
    const intended = scored[0];
    if (!intended) {
      set({ predictedReply: null });
      return;
    }
    const e = epoch;
    try {
      await engine.setPosition(intended.after.fen);
      // `peek: true` — this is a hint, not the robot's actual reply. The
      // fake engine's script drives the robot's committed move (made later
      // in `robotMove`); consuming it here would drain the script and the
      // robot would fall back to `legal[0]` — chess.js's verbose ordering
      // put b8c6 ahead of e7e5, so the golden-path test got '1. e4 Nc6'.
      // Stockfish ignores the flag.
      const [bm] = await engine.getBestMoves(1, { movetimeMs: 200, peek: true });
      if (e !== epoch) return;
      if (!bm) {
        set({ predictedReply: null });
        return;
      }
      // bm.pv is the full PV after the player's intended move. Cap at
      // lookaheadDepth. Each token is a UCI move string ("e7e5"); split
      // into from/to squares.
      const chain: { from: string; to: string }[] = [];
      for (let i = 0; i < bm.pv.length && i < lookaheadDepth; i++) {
        const uci = bm.pv[i];
        if (!uci || uci.length < 4) continue;
        chain.push({ from: uci.slice(0, 2), to: uci.slice(2, 4) });
      }
      set({ predictedReply: chain.length > 0 ? chain : null });
    } catch {
      // Engine hiccup — silently leave predictedReply null; the player still
      // sees the selection + legal-target rings, just without the hint.
      if (e === epoch) set({ predictedReply: null });
    }
  },
}));

/** The move's sound from its SAN: captures thud, castles double-tap, promotions flourish. */
function playMoveSfx(san: string, gameId: GameId, pan: number): void {
  const capture = san.includes('x');
  if (gameId === 'chess') {
    if (san.startsWith('O-O')) playSfx('chess-castle', pan);
    else if (san.includes('=')) playSfx('chess-promote', pan);
    else playSfx(capture ? 'chess-capture' : 'chess-slide', pan);
    if (san.includes('+')) playSfx('chess-check', pan);
  } else {
    playSfx(capture ? 'checkers-jump' : 'checkers-slide', pan);
  }
}

/** The game-over sound, read off the same toast title the player sees. */
function playGameoverSfx(title: string): void {
  if (title.includes('win')) playSfx('win');
  else if (title.includes('lose')) playSfx('lose');
  else playSfx('draw');
}

/** Translate a finished game into a toast title for the player who just played. */
function resultToastTitle(next: PositionSnapshot, playerTurn: 'w' | 'b'): string {
  const r = next.result;
  if (!r) return 'Game over';
  if (r.kind === 'checkmate') {
    // `next.turn` is the side that has no legal move — the LOSER. The winner
    // is the opposite side. So if the player was white and the next-turn is
    // black, white checkmated black → "You win".
    const playerWon = next.turn !== playerTurn;
    return playerWon ? 'You win — checkmate!' : 'You lose — checkmate.';
  }
  if (r.kind === 'elimination') {
    const playerWon = next.turn !== playerTurn;
    return playerWon ? 'You win!' : 'You lose.';
  }
  if (r.kind === 'stalemate' || r.kind === 'draw') return 'Draw';
  return 'Game over';
}
